import * as querystring from 'query-string';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Auth, Venues, AuthProvider, VendorsApi } from '@weddingspot/ws-api-client';

import * as types from '../../types';

import { routeToActiveItem, activeItemToLinkProps, itemToRoute } from '../../components/venue-management/constants';
import { VenueManagement } from '../../components/venue-management';
import { TwsAdmin, ActiveItem } from '../../components/venue-management/types';
import NotFound from '../../components/404';
import { VenueManagementAction, navigateTo, setActiveItemTouched } from '../../actions/venue-management';
import ConfirmModal from '../../components/modal-confirm';

interface RouteMatchProps {
    item: 'general' | 'availability' | 'amenities';
}

interface OwnProps extends RouteComponentProps<RouteMatchProps> {
    activeItem: ActiveItem;
    activeItemTouched: boolean;
    navigateTo: (item: ActiveItem) => void;
    setTouched: (touched: boolean) => void;
    showNavigateWithoutSaveConf: (saveHandler: () => Promise<void>) => void;
}

interface OwnState {
    permissionsValid: boolean | null;
    loadingVenueData: boolean;
    currentVenue: TwsAdmin.AvailableVenue | null;
    availableVenues: TwsAdmin.AvailableVenue[] | null;
    shoudShowNavigateWithoutSaveConf: boolean;
    preNavigateTarget: ActiveItem | null;
}

class VenueManagementContainer extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);

        this.state = {
            permissionsValid: null,
            loadingVenueData: true,
            currentVenue: null,
            availableVenues: null,
            shoudShowNavigateWithoutSaveConf: false,
            preNavigateTarget: null,
        };

        this.checkTouchedAndNavigate = this.checkTouchedAndNavigate.bind(this);
        this.navigateTo = this.navigateTo.bind(this);
        this.showSaveModal = this.showSaveModal.bind(this);
    }

    componentDidMount() {
        // First things first, validate permissions
        AuthProvider.userInfo()
            .then((res: Auth.UserInfo) => {
                this.setState({ permissionsValid: res.is_staff });
                if (res.is_staff) {
                    const vid = this.getVidFromSearch();

                    if (!vid) {
                        this.setState({
                            loadingVenueData: false,
                            currentVenue: null,
                        });
                    } else {
                        // TODO: Replace this w/ a dedicated API
                        VendorsApi.venueDetails(vid)
                            .then((res: Venues.VenueDetails) => {
                                this.setState({
                                    loadingVenueData: false,
                                    currentVenue: {
                                        venue_id: res.id,
                                        venue_name: res.name,
                                    },
                                });
                            })
                            .catch(() => {
                                // Error occurred while fetching venue, just
                                // set to creat venue mode
                                this.setState({
                                    loadingVenueData: false,
                                    currentVenue: null,
                                });
                            });
                    }
                }
            })
            .catch(() => {
                this.setState({ permissionsValid: false });
            });
    }

    render() {
        if (!this.props.match) {
            // shouldn't happen unless routes are f'd
            return <NotFound />;
        }

        const item = this.props.activeItem;

        if (!item) {
            // shouldn't happen unless routes are f'd
            return <NotFound />;
        }

        // Unknown permission state, just render nothing
        if (this.state.permissionsValid === null) {
            return <div />;
        }

        if (this.state.permissionsValid === false) {
            return <NotFound />;
        }

        // Don't allow the user to access any other page other than general
        if (!this.state.loadingVenueData && !this.state.currentVenue && item !== ActiveItem.GENERAL) {
            return <NotFound />;
        }

        // Trigger navigation if route match props change (could happen if user is using back/fwd button)
        const urlItemToken = this.props.match.params.item;
        const urlItem = routeToActiveItem(urlItemToken);

        if (!!urlItem && urlItem !== this.props.activeItem) {
            this.navigateTo(urlItem);
        }

        return (
            <div>
                <VenueManagement
                    activeItem={item}
                    loadingVenuesData={this.state.loadingVenueData}
                    currentVenue={this.state.currentVenue}
                    availableVenues={this.state.availableVenues}
                    onCreateNewVenue={(id, name) => this.onCreateVenue(id, name)}
                    navigateTo={this.checkTouchedAndNavigate}
                    setTouched={this.props.setTouched}
                />
                {this.state.shoudShowNavigateWithoutSaveConf && (
                    <ConfirmModal
                        header='You have unsaved changes'
                        content="Would you like to save the changes you've made before continuing?"
                        primaryCta='Yes'
                        secondaryCta='No'
                        primaryAsync={false}
                        onPrimaryCtaClick={() => this.showSaveModal(false, null)}
                        onSecondaryCtaClick={() => {
                            if (!!this.state.preNavigateTarget) {
                                this.navigateTo(this.state.preNavigateTarget);
                            }
                            this.showSaveModal(false, null);
                            this.props.setTouched(false);
                        }}
                    />
                )}
            </div>
        );
    }

    /**
     * Update currently editing venue if a new one is created
     * after form submission
     */
    private onCreateVenue(id: number, name: string) {
        this.setState({
            currentVenue: {
                venue_id: id,
                venue_name: name,
            },
        });

        // Update URL params without navigating
        if (typeof window !== 'undefined') {
            const qps = querystring.parse(this.props.location.search);
            let newQps = { ...qps };
            newQps.vid = `${id}`;
            window.history.pushState(null, '', `${window.location.pathname}?${querystring.stringify(newQps)}`);
        }
    }

    private checkTouchedAndNavigate(target: ActiveItem) {
        if (target !== this.props.activeItem) {
            // Dispatch confirmation modal instead
            if (this.props.activeItemTouched) {
                // TODO: Show homebrew modal
                this.showSaveModal(true, target);
            } else {
                this.navigateTo(target);
            }
        }
    }

    private showSaveModal(show: boolean, target: ActiveItem | null) {
        this.setState({
            shoudShowNavigateWithoutSaveConf: show,
            preNavigateTarget: target,
        });
    }

    private navigateTo(item: ActiveItem) {
        if (this.props.activeItem !== item) {
            const vid = this.getVidFromSearch();
            const targetRouteDetails = activeItemToLinkProps(item, vid || undefined);
            let route = targetRouteDetails.pathname;
            if (!!targetRouteDetails.search) {
                route += targetRouteDetails.search;
            }
            this.props.history.push(route);
            this.props.navigateTo(item);
        }
    }

    private getVidFromSearch() {
        const qps = querystring.parse(this.props.location.search);
        let vid;
        if (!qps.vid) {
            return null;
        } else if (typeof qps.vid === 'object') {
            vid = parseInt(`${qps.vid[0]}`, 10);
        } else {
            vid = parseInt(`${qps.vid}`, 10);
        }
        if (vid === NaN) {
            return null;
        }

        return vid;
    }
}

const mapStateToProps = (appState: types.StoreState) => {
    return { ...appState.venueManagement };
};

const mapDispatchToProps = (dispatch: Dispatch<VenueManagementAction>) => ({
    navigateTo: (item: ActiveItem) => {
        navigateTo(item)(dispatch);
    },
    setTouched: (touched: boolean) => setActiveItemTouched(touched)(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(VenueManagementContainer);
